<template>
  <ActionMenu
    class="doc-library-menu"
    :class="{ 'doc-library-menu--lighter': lighter }"
    :position="position"
  >
    <template #button>
      <slot></slot>
    </template>

    <Action
      v-if="doc.user.list === 'reading'"
      icon="ireading"
      @click.prevent="markDocFinishedWithUndo(doc, docsView)"
    >
      Move to Finished
    </Action>

    <Action
      v-if="doc.user.list == 'finished'"
      icon="ireading"
      @click.prevent="moveTo('reading')"
    >
      Move to Reading Now
    </Action>

    <Action
      v-if="!doc.user.list || doc.user.list == 'removed'"
      icon="ireading"
      @click.prevent="moveTo('reading')"
    >
      Add to Reading Now
    </Action>

    <Action
      v-if="markNotStarted20321B && canMarkNotStarted(doc)"
      icon="ireset"
      @click.prevent="markDocNotStartedWithUndo(doc, docsView)"
    >
      Mark as Not Started
    </Action>
    <Action
      v-else-if="
        !markNotStarted20321B && doc.user.list && doc.user.list != 'removed'
      "
      icon="ilibrary-remove"
      @click.prevent="moveTo('removed')"
    >
      Remove from My Library
    </Action>

    <Action
      v-if="isSingleDocPage && doc.amazon_link && isAndroidDevice === false"
      icon="icart-buy"
      :href="doc.amazon_link"
    >
      Buy on Amazon
    </Action>
  </ActionMenu>
</template>

<script setup lang="ts">
import { canMarkNotStarted, DocsView, DocView } from '@/models/doc'
import {
  markDocFinishedWithUndo,
  markDocNotStartedWithUndo,
} from '@/models/doc.read.state'
import type { UserDocListItem } from '@/models/interfaces'
import { platform } from '@/services/platform'
import { ref, onMounted } from 'vue'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'

/**
 * This component is a wrapper on top of `ActionMenu` with
 * actions related to moving docs between the My Library
 * standard lists ("Reading Now", "Favorites" and "Finished").
 *
 * It can be used in a doc listing page by passing the `docsView`
 * prop, or in a single doc page, by passing a `DocView` instance to
 * the `doc` prop.
 *
 * @param doc - The doc upon which these actions will take effect.
 *   In a listing page, it can be a `DocListItem` (but must be paired
 *   with the `docsView` prop).
 *   In a single doc page, it can be a `DocView` instance.
 * @param docsView - A `DocsView` instance. This prop should be used when
 *   `doc` is not an instance of `DocsView`.
 * @param position - The aligment of the dropdown list relative to the button.
 */
const props = defineProps<{
  doc: UserDocListItem | DocView
  docsView?: DocsView
  position?: 'center' | 'right'
  lighter?: boolean
  /**
   * Replace "Remove from My Library" with "Mark as Not Started", which keeps
   * the doc's progress (#20321 variant B).
   */
  markNotStarted20321B?: boolean
}>()

const isSingleDocPage = props.doc instanceof DocView
const isListPage = props.docsView && !isSingleDocPage

if (!isSingleDocPage && !isListPage) {
  throw new Error(
    'Incorrect usage of the component.' +
      'If `doc` is not an instance of `DocView`,' +
      'then `docsView` must not be undefined.',
  )
}
// Detect if user platform is Android to hide Amazon link
// Because Google Play does not like that we have an external link
// where people can by something, see #16533.
const isAndroidDevice = ref(false)

onMounted(async () => {
  isAndroidDevice.value = await platform.isNativeAndroidApp()
})

async function moveTo(list: string): Promise<void> {
  if (isSingleDocPage) {
    await props.doc.moveTo(list)
  } else if (isListPage) {
    await props.docsView.moveTo(props.doc, list)
  }
}
</script>

<style lang="scss" scoped>
.doc-library-menu {
  --button-color: #{$c-black-9};
  --button-background-hover: #{rgba($c-black-2, 0.05)};
}

.doc-library-menu--lighter {
  --button-color: #{$c-black-6};
}

.night-mode .doc-library-menu {
  --button-background-hover: #{rgba($c-dark-3, 0.5)};
}
</style>
